import {
  IconArrowLeft as ArrowLeft,
  IconCheck as Check,
  IconMinus as Minus,
} from "@tabler/icons-react";
import { SectionHeader } from "../components/shared/SectionHeader";

export function PricingPage({ t }) {
  return (
    <main>
      <section className="relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
        <div
          className="orb"
          style={{
            width: 440,
            height: 440,
            background: "hsl(var(--brand) / 0.18)",
            top: 80,
            right: -140,
          }}
        />

        <div className="container-x relative">
          <a href="/" className="btn-pill btn-ghost mb-10 w-fit text-sm">
            <ArrowLeft className="h-4 w-4" />
            {t.pricing.back}
          </a>

          <SectionHeader eyebrow={t.pricing.eyebrow} title={t.pricing.title} />
          <p className="reveal mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
            {t.pricing.lead}
          </p>

          <div className="reveal mt-12 grid gap-6 lg:grid-cols-2">
            {t.pricing.plans.map((plan) => (
              <article
                key={plan.name}
                className="overflow-hidden rounded-3xl border border-brand bg-[hsl(var(--surface))] shadow-xl shadow-[hsl(var(--brand)/0.06)]"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="font-sans text-2xl font-bold leading-tight">{plan.name}</h2>
                      <p className="mt-2 text-base font-medium text-muted-foreground">{plan.ideal}</p>
                    </div>
                    {/* {plan.primary ? (
                      <span className="rounded-full bg-brand px-3 py-1 font-sans text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white">
                        {t.pricing.badge}
                      </span>
                    ) : null} */}
                  </div>

                  <div className="mt-8">
                    <span className="inline-flex rounded-full border border-brand/30 bg-brand-soft px-3 py-1 font-sans text-[0.68rem] font-bold uppercase tracking-[0.16em] text-brand">
                      {t.pricing.introductoryPrice}
                    </span>
                    <div className="mt-3 flex items-end gap-2">
                      <div className="font-sans text-4xl font-bold tracking-tight">{plan.price}</div>
                      <div className="pb-1 font-sans text-lg font-semibold text-[hsl(var(--foreground))]">
                        {plan.period}
                      </div>
                    </div>
                  </div>

                  <PlanCta plan={plan} />
                </div>

                <div className="border-t border-[hsl(var(--border))] p-6 md:p-8">
                  <PricingFeatureGroup
                    title={t.pricing.included}
                    features={plan.features.slice(0, 3)}
                  />
                  <PricingFeatureGroup
                    title={t.pricing.features}
                    features={plan.features.slice(3)}
                    className="mt-8"
                  />
                </div>
              </article>
            ))}
          </div>

          <div className="reveal mt-8 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface)/0.72)] p-6 md:p-7">
            <div className="flex gap-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand text-white">
                <Check className="h-5 w-5" />
              </span>
              <p className="text-base leading-relaxed text-muted-foreground">{t.pricing.note}</p>
            </div>
          </div>

          <PricingAddOns addOns={t.pricing.addOns} />

          <PricingComparison pricing={t.pricing} />
        </div>
      </section>
    </main>
  );
}

function PricingFeatureGroup({ title, features, className = "" }) {
  return (
    <div className={className}>
      <h3 className="font-sans text-base font-bold">{title}</h3>
      <ul className="mt-3 space-y-3">
        {features.map((feature) => (
          <li key={feature.label} className="flex gap-3 text-sm leading-snug">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />
            <span>
              <strong className="font-semibold">{feature.value}</strong>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PricingComparison({ pricing }) {
  const [core, enterprise] = pricing.plans;

  return (
    <section className="reveal mt-20">
      <div className="max-w-3xl">
        <h2 className="font-display text-[clamp(2rem,4.5vw,3.75rem)] font-medium leading-[1.25]">
          {pricing.comparison.title}
        </h2>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-3 md:items-end">
        <div className="hidden md:block" />
        {[core, enterprise].map((plan) => (
          <div key={plan.name} className="rounded-2xl bg-[hsl(var(--surface))] p-5">
            <h3 className="font-sans text-xl font-bold">{plan.name}</h3>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              {plan.price} / {plan.period}
            </p>
            <PlanCta plan={plan} />
          </div>
        ))}
      </div>

      <div className="mt-8 overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 md:p-6">
        <div className="divide-y divide-[hsl(var(--border))]">
          {pricing.comparison.rows.map((row) =>
            row.section ? (
              <div key={row.label} className="px-2 pb-3 pt-7 first:pt-2">
                <h3 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-brand">
                  {row.label}
                </h3>
              </div>
            ) : (
              <div
                key={row.label}
                className="grid gap-3 px-2 py-4 text-sm md:grid-cols-3 md:items-center md:py-5"
              >
                <div className="font-medium">{row.label}</div>
                <ComparisonValue value={row.core} />
                <ComparisonValue value={row.enterprise} />
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function PricingAddOns({ addOns }) {
  if (!addOns) {
    return null;
  }

  return (
    <div className="mt-8 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface)/0.78)] p-5 md:p-6">
      <div className="grid gap-6 lg:grid-cols-[1.1fr_2fr] lg:items-center">
        <div>
          <h3 className="font-sans text-xl font-bold">{addOns.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{addOns.desc}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {addOns.items.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4"
            >
              <div className="text-sm font-medium text-muted-foreground">{item.label}</div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-sans text-2xl font-bold">{item.value}</span>
                {item.rate ? (
                  <span className="text-xs font-semibold text-muted-foreground">{item.rate}</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ComparisonValue({ value }) {
  if (!value) {
    return <div className="hidden md:block" />;
  }

  if (value === "check") {
    return (
      <span className="flex justify-start md:justify-center">
        <Check className="h-5 w-5 text-brand" strokeWidth={2.4} />
      </span>
    );
  }

  if (value === "minus") {
    return (
      <span className="flex justify-start md:justify-center">
        <Minus className="h-5 w-5 text-muted-foreground" strokeWidth={2.4} />
      </span>
    );
  }

  return <div className="font-medium md:text-center">{value}</div>;
}

function PlanCta({ plan }) {
  const isExternal = /^https?:\/\//.test(plan.href);

  return (
    <a
      href={plan.href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
      className={`btn-pill mt-6 w-full ${plan.primary ? "btn-primary" : "btn-ghost"}`}
    >
      {plan.cta}
    </a>
  );
}
